<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseChart from '@/components/charts/BaseChart.vue';
import MaterialIcon from '@/components/common/MaterialIcon.vue';
import SectionTitle from '@/components/common/section-title.vue';
import { AnimatedContent } from '@/components/vuebits';

const { t } = useI18n();

const tickets = ref(10);
const teamSize = ref(3);
const annualCost = ref(60000);
const adoptionRate = ref(35);

/** 工单负载系数下限：即使工单量很少，AI 仍可承担基础重复工作 */
const WORKLOAD_MIN = 0.25;
/** 工单负载系数上限：再高也不超过 75% 的人力可替代比例 */
const WORKLOAD_MAX = 0.75;
/** 工单量归一化分母，控制负载系数随工单量增长的速度 */
const WORKLOAD_TICKET_DIVISOR = 400;

const inputs = computed(() => [
  {
    id: 'tickets',
    label: t('calculator.tickets'),
    model: tickets,
    min: 1,
    max: 200,
    step: 1,
    format: (v) => `${v}`,
    suffix: t('calculator.unitTickets'),
  },
  {
    id: 'team-size',
    label: t('calculator.teamSize'),
    model: teamSize,
    min: 1,
    max: 50,
    step: 1,
    format: (v) => `${v}`,
    suffix: t('calculator.unitPeople'),
  },
  {
    id: 'annual-cost',
    label: t('calculator.annualCost'),
    model: annualCost,
    min: 10000,
    max: 500000,
    step: 5000,
    format: (v) => `¥${v.toLocaleString()}`,
    suffix: t('calculator.unitPerPerson'),
  },
  {
    id: 'adoption-rate',
    label: t('calculator.adoptionRate'),
    model: adoptionRate,
    min: 1,
    max: 100,
    step: 1,
    format: (v) => `${v}%`,
    suffix: '',
  },
]);

/**
 * 计算工单负载系数
 * 公式：min(上限, 下限 + 每日工单量 / 归一化分母)
 */
const workloadFactor = computed(() => {
  const raw = WORKLOAD_MIN + tickets.value / WORKLOAD_TICKET_DIVISOR;
  return Math.min(WORKLOAD_MAX, raw);
});

/** 团队年度总人力成本 = 人数 × 人均年成本 */
const totalTeamCost = computed(() => teamSize.value * annualCost.value);

/** 有效节省比例 = (AI 采纳率 / 100) × 工单负载系数 */
const effectiveSavingsRate = computed(
  () => (adoptionRate.value / 100) * workloadFactor.value,
);

/** 年度预估节省 = 团队总成本 × 有效节省比例 */
const savings = computed(() =>
  Math.round(totalTeamCost.value * effectiveSavingsRate.value),
);

/** 结果区数字过渡，避免 remount 导致循环 */
const displayedSavings = ref(0);
let savingsRafId = null;
const SAVINGS_ANIM_MS = 320;

function animateSavings(target) {
  if (savingsRafId) {
    cancelAnimationFrame(savingsRafId);
    savingsRafId = null;
  }

  const start = displayedSavings.value;
  if (start === target) return;

  const startTime = performance.now();

  const tick = (now) => {
    const progress = Math.min(1, (now - startTime) / SAVINGS_ANIM_MS);
    const eased = 1 - (1 - progress) ** 3;
    displayedSavings.value = Math.round(start + (target - start) * eased);

    if (progress < 1) {
      savingsRafId = requestAnimationFrame(tick);
    } else {
      displayedSavings.value = target;
      savingsRafId = null;
    }
  };

  savingsRafId = requestAnimationFrame(tick);
}

watch(savings, animateSavings, { immediate: true });

const monthlySavings = computed(() => Math.round(savings.value / 12));

const perPersonSavings = computed(() =>
  teamSize.value > 0 ? Math.round(savings.value / teamSize.value) : 0,
);

const roiPercent = computed(() =>
  Math.round(effectiveSavingsRate.value * 1000) / 10,
);

const remainingCost = computed(() =>
  Math.max(0, totalTeamCost.value - savings.value),
);

const breakdown = computed(() => [
  {
    label: t('calculator.totalTeamCost'),
    value: `¥${totalTeamCost.value.toLocaleString()}`,
  },
  {
    label: t('calculator.workloadFactor'),
    value: `${Math.round(workloadFactor.value * 100)}%`,
  },
  {
    label: t('calculator.effectiveRate'),
    value: `${Math.round(effectiveSavingsRate.value * 1000) / 10}%`,
  },
]);

const quickStats = computed(() => [
  {
    label: t('calculator.monthlyLabel'),
    value: `¥${monthlySavings.value.toLocaleString()}`,
  },
  {
    label: t('calculator.perPerson'),
    value: `¥${perPersonSavings.value.toLocaleString()}`,
  },
  {
    label: t('calculator.roi'),
    value: `${roiPercent.value}%`,
  },
]);

/** 柱状图：总成本 / 预计节省 / 剩余成本，随滑块联动 */
const isNarrow = ref(typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
let narrowMq = null;

function syncNarrow(event) {
  isNarrow.value = event?.matches ?? narrowMq?.matches ?? false;
}

const chartHeight = computed(() => (isNarrow.value ? '200px' : '240px'));

const chartOption = computed(() => {
  const narrow = isNarrow.value;

  return {
    color: ['#94a3b8', '#155eef', '#cbd5e1'],
    grid: {
      left: narrow ? 0 : 4,
      right: narrow ? 0 : 4,
      top: narrow ? 16 : 28,
      bottom: narrow ? 0 : 4,
      containLabel: true,
    },
    legend: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: '#e2e8f0',
      borderWidth: 1,
      textStyle: { color: '#0f172a', fontSize: 12 },
      valueFormatter: (v) => `¥${Number(v).toLocaleString()}`,
      confine: true,
    },
    xAxis: {
      type: 'category',
      data: [
        t('calculator.chart.total'),
        t('calculator.chart.savings'),
        t('calculator.chart.remaining'),
      ],
      axisLine: { lineStyle: { color: '#e2e8f0' } },
      axisTick: { show: false },
      axisLabel: {
        color: '#64748b',
        fontSize: narrow ? 10 : 12,
        interval: 0,
        hideOverlap: true,
      },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
      axisLabel: {
        color: '#94a3b8',
        fontSize: narrow ? 10 : 11,
        formatter: (v) => {
          if (v >= 10000) return `${Math.round(v / 1000)}k`;
          return `${v}`;
        },
      },
    },
    series: [
      {
        name: t('calculator.chart.series'),
        type: 'bar',
        barWidth: narrow ? 28 : 36,
        data: [
          {
            value: totalTeamCost.value,
            itemStyle: { color: '#94a3b8', borderRadius: [4, 4, 0, 0] },
          },
          {
            value: savings.value,
            itemStyle: { color: '#155eef', borderRadius: [4, 4, 0, 0] },
          },
          {
            value: remainingCost.value,
            itemStyle: { color: '#cbd5e1', borderRadius: [4, 4, 0, 0] },
          },
        ],
      },
    ],
  };
});

function sliderProgress(value, min, max) {
  return ((value - min) / (max - min)) * 100;
}

onMounted(() => {
  narrowMq = window.matchMedia('(max-width: 767px)');
  syncNarrow(narrowMq);
  narrowMq.addEventListener('change', syncNarrow);
});

onBeforeUnmount(() => {
  if (savingsRafId) cancelAnimationFrame(savingsRafId);
  narrowMq?.removeEventListener('change', syncNarrow);
  narrowMq = null;
});

</script>

<template>
  <section
    id="calculator"
    class="relative overflow-hidden pb-[clamp(4rem,10vw,8rem)] pt-14 md:pt-20"
  >
    <div class="section-shell relative">
      <AnimatedContent :distance="24">
        <p class="eyebrow text-center">{{ t('calculator.eyebrow') }}</p>
      </AnimatedContent>
      <SectionTitle :title="t('calculator.title')" />
      <AnimatedContent :delay="0.05" :distance="20" class-name="-mt-7">
        <p class="mx-auto max-w-2xl text-center text-[15px] leading-7 text-muted-foreground sm:text-base md:text-lg">
          {{ t('calculator.subtitle') }}
        </p>
      </AnimatedContent>

      <AnimatedContent :delay="0.1" :distance="28" class-name="mt-10 md:mt-14">
        <!-- 手机粘性结果条：调参时始终可见 -->
        <div
          class="sticky top-16 z-20 -mx-1 mb-6 rounded-lg border border-border/70 bg-background/95 px-3.5 py-3 shadow-sm backdrop-blur-md md:hidden"
        >
          <div class="flex items-baseline justify-between gap-3">
            <span class="text-xs font-medium text-muted-foreground">{{ t('calculator.resultLabel') }}</span>
            <span class="text-xl font-bold tabular-nums tracking-tight text-foreground">
              ¥{{ displayedSavings.toLocaleString() }}
            </span>
          </div>
        </div>

        <!-- md+ 保持参数 → 结果原序；lg 左右分栏 -->
        <div class="grid items-start gap-10 md:gap-12 lg:grid-cols-12 lg:gap-16">
          <!-- 参数 -->
          <div class="lg:col-span-5">
            <p class="text-sm font-medium text-primary">{{ t('calculator.inputsTitle') }}</p>
            <p class="mt-1.5 text-sm text-muted-foreground md:mt-2">{{ t('calculator.inputsHint') }}</p>

            <div class="mt-6 border-t border-slate-200/80 md:mt-8">
              <div
                v-for="input in inputs"
                :key="input.id"
                class="border-b border-slate-200/80 py-4 md:py-5"
              >
                <div class="mb-3 flex items-start justify-between gap-3 md:mb-4 md:items-baseline md:gap-4">
                  <label
                    :for="input.id"
                    class="min-w-0 text-sm font-medium leading-snug text-foreground md:text-[15px]"
                  >
                    {{ input.label }}
                  </label>
                  <span class="shrink-0 text-right text-[15px] font-semibold tabular-nums tracking-tight text-primary md:text-base">
                    {{ input.format(input.model.value) }}
                    <span
                      v-if="input.suffix"
                      class="ml-0.5 text-[11px] font-medium text-slate-400 md:ml-1 md:text-xs"
                    >{{ input.suffix }}</span>
                  </span>
                </div>

                <div class="slider-track-shell">
                  <input
                    :id="input.id"
                    v-model.number="input.model.value"
                    type="range"
                    :min="input.min"
                    :max="input.max"
                    :step="input.step"
                    class="calc-slider w-full"
                    :style="{ '--progress': `${sliderProgress(input.model.value, input.min, input.max)}%` }"
                  />
                </div>

                <div class="mt-2 flex justify-between text-[11px] tabular-nums text-slate-400">
                  <span>{{ input.format(input.min) }}</span>
                  <span>{{ input.format(input.max) }}</span>
                </div>
              </div>
            </div>

            <a
              href="#plans"
              class="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-[gap] hover:gap-2.5 md:mt-10"
            >
              {{ t('calculator.viewPlans') }}
              <MaterialIcon name="arrow_forward" class="text-base" />
            </a>
            <p class="mt-3 max-w-md text-xs leading-5 text-muted-foreground md:mt-4">
              {{ t('calculator.disclaimer') }}
            </p>
          </div>

          <!-- 结果 -->
          <div class="lg:col-span-7">
            <p class="text-sm font-medium text-primary">{{ t('calculator.resultLabel') }}</p>

            <p
              class="mt-3 text-[clamp(2.25rem,10vw,3.75rem)] font-bold leading-none tracking-tight text-foreground tabular-nums md:mt-4"
            >
              ¥{{ displayedSavings.toLocaleString() }}
            </p>
            <p class="mt-2 text-sm text-muted-foreground md:mt-3">{{ t('calculator.annualSuffix') }}</p>

            <!-- 指标：手机分行，md+ 基线横排 -->
            <dl
              class="mt-5 flex flex-col gap-3 border-y border-slate-200/80 py-5 md:flex-row md:flex-wrap md:items-baseline md:gap-x-10 md:gap-y-3 md:py-6"
            >
              <div
                v-for="stat in quickStats"
                :key="stat.label"
                class="flex items-baseline justify-between gap-3 md:justify-start md:gap-2"
              >
                <dt class="text-sm text-muted-foreground md:sr-only">{{ stat.label }}</dt>
                <dd class="text-lg font-bold tracking-tight text-foreground tabular-nums md:text-xl">
                  {{ stat.value }}
                </dd>
                <span class="hidden text-sm text-muted-foreground md:inline">{{ stat.label }}</span>
              </div>
            </dl>

            <div class="mt-7 md:mt-8">
              <p class="mb-3 text-sm font-medium text-foreground md:text-[15px]">
                {{ t('calculator.chartTitle') }}
              </p>
              <BaseChart :option="chartOption" :height="chartHeight" />
            </div>

            <div class="mt-8 md:mt-10">
              <p class="text-sm font-medium text-slate-400">{{ t('calculator.breakdown') }}</p>
              <ul class="mt-4 space-y-3 md:mt-5 md:space-y-3.5">
                <li
                  v-for="item in breakdown"
                  :key="item.label"
                  class="flex items-center justify-between gap-3 text-sm md:gap-4 md:text-[15px]"
                >
                  <span class="flex min-w-0 items-center gap-2.5 text-slate-600 md:gap-3">
                    <span class="h-px w-3 shrink-0 bg-primary/70" />
                    <span class="truncate">{{ item.label }}</span>
                  </span>
                  <span class="shrink-0 font-semibold tabular-nums text-foreground">{{ item.value }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </AnimatedContent>
    </div>
  </section>
</template>

<style scoped>
.slider-track-shell {
  padding-block: 6px;
}

@media (max-width: 767px) {
  .calc-slider {
    --thumb-size: 22px;
    --track-h: 2px;
    touch-action: pan-y;
  }

  .slider-track-shell {
    padding-block: 10px;
  }
}

.calc-slider {
  --track-h: 1px;
  --thumb-size: 16px;
  --progress: 0%;

  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  height: var(--thumb-size);
  margin: 0;
  background: transparent;
  cursor: grab;
  touch-action: none;
}

.calc-slider:active {
  cursor: grabbing;
}

.calc-slider::-webkit-slider-runnable-track {
  height: var(--track-h);
  border-radius: 999px;
  background:
    linear-gradient(90deg,
      #155eef 0%,
      #155eef var(--progress),
      #e2e8f0 var(--progress),
      #e2e8f0 100%);
}

.calc-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: var(--thumb-size);
  height: var(--thumb-size);
  margin-top: calc((var(--track-h) - var(--thumb-size)) / 2);
  border: 2px solid #155eef;
  border-radius: 999px;
  background: #fff;
  transition: transform 0.2s ease;
}

.calc-slider:hover::-webkit-slider-thumb,
.calc-slider:active::-webkit-slider-thumb {
  transform: scale(1.1);
}

.calc-slider::-moz-range-track {
  height: var(--track-h);
  border: none;
  border-radius: 999px;
  background: #e2e8f0;
}

.calc-slider::-moz-range-progress {
  height: var(--track-h);
  border-radius: 999px;
  background: #155eef;
}

.calc-slider::-moz-range-thumb {
  width: var(--thumb-size);
  height: var(--thumb-size);
  border: 2px solid #155eef;
  border-radius: 999px;
  background: #fff;
}

.calc-slider:focus-visible {
  outline: none;
}

.calc-slider:focus-visible::-webkit-slider-thumb {
  box-shadow: 0 0 0 3px rgb(21 94 239 / 18%);
}

.calc-slider:focus-visible::-moz-range-thumb {
  box-shadow: 0 0 0 3px rgb(21 94 239 / 18%);
}
</style>
